import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from './../material.module';
import { AppStateService } from './../app-state.service';
import { FormsModule } from '@angular/forms';
import { FormConfigService } from '../registration/forms-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss']
})
export class DocumentsComponent implements OnInit {
  clientId: string;
  patientId: string;
  searchQuery: string = '';
  documents: any[] = [];
  filledForms: any[] = [];
  loading: boolean = false;
  displayedColumns: string[] = ['icon', 'fileName', 'documentType', 'uploadDate', 'actions'];

  constructor(
    private appStateService: AppStateService,
    private router: Router,
    private formConfigService: FormConfigService,
    private snackBar: MatSnackBar
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
  }

  ngOnInit(): void {
    this.fetchDocuments();
    this.fetchFilledForms();
  }

  fetchDocuments(): void {
    this.loading = true;

    this.formConfigService.fetchDocuments(this.clientId, this.patientId).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.documents = response.documents || [];
        // Sort by upload date, newest first
        this.documents.sort((a: any, b: any) => {
          const dateA = new Date(a.uploadDate || 0).getTime();
          const dateB = new Date(b.uploadDate || 0).getTime();
          return dateB - dateA;
        });
      },
      error: (error: any) => {
        this.loading = false;
        this.showError("Error fetching documents.");
      }
    });
  }

  fetchFilledForms(): void {
    this.formConfigService.getAllPatientForms(this.clientId, this.patientId).subscribe({
      next: (forms: any) => {
        this.filledForms = forms.sort((a: any, b: any) =>
          new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime()
        );
      },
      error: (error: any) => {
      }
    });
  }

  showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  getDocumentIcon(doc: any): string {
    if (doc.isImage) {
      return 'image';
    } else if (doc.isPdf || doc.mimeType === 'application/pdf') {
      return 'picture_as_pdf';
    } else if (doc.mimeType?.includes('word')) {
      return 'description';
    } else if (doc.mimeType?.includes('excel') || doc.mimeType?.includes('spreadsheet')) {
      return 'table_chart';
    } else {
      return 'insert_drive_file';
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  viewDocument(doc: any): void {
    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, '_blank');
    } else {
      // Use the download endpoint
      this.downloadDocument(doc);
    }
  }

  downloadDocument(doc: any): void {
    this.formConfigService.downloadDocument(this.clientId, this.patientId, doc.documentId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.fileName || `document_${doc.documentId}`;
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
      },
      error: (error: any) => {
        this.showError('Error downloading document');
      }
    });
  }

  viewFormPdf(form: any): void {
    this.formConfigService.fetchFormPdf(this.clientId, this.patientId, form.form_id).subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: (error: any) => {
        this.showError('Error fetching PDF');
      }
    });
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard', 'main']);
  }

  filterDocuments(searchTerm: string): void {
    this.searchQuery = searchTerm;
  }

  get filteredDocuments(): any[] {
    if (!this.searchQuery) {
      return this.documents;
    }

    const query = this.searchQuery.toLowerCase();
    return this.documents.filter(doc =>
      doc.fileName?.toLowerCase().includes(query) ||
      doc.documentType?.toLowerCase().includes(query) ||
      doc.description?.toLowerCase().includes(query) ||
      doc.category?.toLowerCase().includes(query)
    );
  }
}